import { MenuItem } from "@material-ui/core";
import { Field, Form, FormikErrors, FormikTouched } from "formik";
import * as React from "react";
import { abbreviatedLanguageOptions, nestedTemplateOptions } from "../../../constants";
import { t } from "../../../i18n/util";
import { PatchApplicantDetailsResponseLanguageEnum } from "../../../network/APITypes";
import { THEME, isDal } from "../../../theme/ThemeStore";
import { IPostApplicationStateTransitionPayload, TemplateEnum } from "../../../types/models";
import { useMobileMediaQuery } from "../../hooks/useMobileMediaQuery";
import { CustomCascadingMenu } from "../../ui/CustomCascadingMenu";
import { CustomCheckbox } from "../../ui/CustomCheckbox";
import { CustomInputField } from "../../ui/CustomInputField";
import { ErrorMessagesList, FormButtons } from "../../ui/Primitives";
import { templateToString } from "../../util/helpers";
import { authStore } from "../../../stores/AuthStore";
import { CustomDatePicker } from "../../ui/CustomDatePicker";

export const CheckBoxDiv = (props: { children: React.ReactNode }) => (
    <div
        style={{
            display: "flex",
            alignItems: "center",
            marginTop: 10,
            gap: 10,
            color: THEME.customColors.headingsDark
        }}
        className="body1"
    >
        {props.children}
    </div>
);

type Props = {
    values: IPostApplicationStateTransitionPayload;
    setFieldValue: (field: string, value: any, shouldValidate?: boolean | undefined) => void;
    onBack: (values: IPostApplicationStateTransitionPayload) => void;
    cancel: boolean;
    firstName: string;
    allowScheduledEmail?: boolean;
    errors: FormikErrors<IPostApplicationStateTransitionPayload>;
    touched: FormikTouched<IPostApplicationStateTransitionPayload>;
};

export const TransferMessageForm = ({
    values,
    setFieldValue,
    onBack,
    cancel,
    firstName,
    allowScheduledEmail,
    errors,
    touched
}: Props) => {
    const isMobile = useMobileMediaQuery();

    return (
        <Form
            style={{
                flexGrow: 1,
                display: "flex",
                padding: 24,
                gap: 16,
                flexDirection: "column"
            }}
        >
            <div style={{ display: "flex", width: "100%", gap: 16 }}>
                {!isDal() && (
                    <Field
                        component={CustomInputField}
                        label={t("screen.dashboard.dialog.language")}
                        name="language"
                        select
                        required
                        style={{
                            flex: 1
                        }}
                        disabled={values.noMessage}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                            setFieldValue(
                                "message",
                                templateToString(
                                    event.target.value as PatchApplicantDetailsResponseLanguageEnum,
                                    values.template,
                                    { firstName, userName: authStore.givenName ?? "" }
                                )
                            );
                        }}
                    >
                        {abbreviatedLanguageOptions.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                                {t(option.messageId)}
                            </MenuItem>
                        ))}
                    </Field>
                )}
                <Field
                    component={CustomCascadingMenu}
                    label={t("screen.dashboard.dialog.template")}
                    name="template"
                    setFieldValue={setFieldValue}
                    required
                    onChange={(newValue: string) => {
                        setFieldValue(
                            "message",
                            templateToString(values.language, newValue as TemplateEnum, {
                                firstName,
                                userName: authStore.givenName ?? ""
                            })
                        );
                    }}
                    options={nestedTemplateOptions()}
                    style={{
                        flex: 3
                    }}
                    disabled={values.noMessage}
                ></Field>
            </div>
            <Field
                component={CustomInputField}
                label={t("screen.dashboard.dialog.message")}
                name="message"
                multiline
                disabled={values.noMessage}
                rows={isMobile ? 12 : 14}
                style={{ width: "100%" }}
            />
            <CheckBoxDiv>
                <Field type="checkbox" name="noMessage" component={CustomCheckbox} checked={values.noMessage} />
                {t("screen.dashboard.dialog.withoutMessage")}
            </CheckBoxDiv>

            {allowScheduledEmail && (
                <>
                    <CheckBoxDiv>
                        <Field
                            type="checkbox"
                            name="scheduledEmail"
                            component={CustomCheckbox}
                            checked={!!values.scheduledEmail}
                        />
                        {t("scheduleEmail.checkbox")}
                    </CheckBoxDiv>
                    {values.scheduledEmail && (
                        <Field
                            component={CustomDatePicker}
                            label={t("scheduleEmail.label")}
                            name="scheduledEmailDate"
                            disablePast
                            style={{ width: "100%", marginBottom: isMobile ? 10 : 32 }}
                        />
                    )}
                </>
            )}

            <ErrorMessagesList errors={errors} touched={touched} />

            <FormButtons
                labelBack={cancel ? t("screen.dataUpload.dialog.cancel") : t("screen.details.button.back")}
                labelSubmit={t("screen.dashboard.move")}
                onBack={() => onBack(values)}
                style={{
                    justifyContent: "flex-end",
                    marginBottom: 0
                }}
            />
        </Form>
    );
};
